import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Row,
  Col,
  Button,
  Typography,
  Space,
  Tag,
  Select,
  Card,
  Tooltip,
  message,
  Collapse,
} from "antd";
import { IoCloseSharp } from "react-icons/io5";
import { MdDone } from "react-icons/md";
import {
  FiPackage,
  FiTag,
  FiBox,
  FiCpu,
  FiAlertTriangle,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import * as selectors from "../selectors";
import {
  getDistrictFilters,
  getSamplerFilters,
  getStateFilters,
} from "../helpers";
import { actions } from "../slice";
import SuccessModal from "./SuccessModal";
const { Title, Text } = Typography;
const { Panel } = Collapse;

const AssetSummaryCard = ({ icon, label, count, color }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "12px 16px",
      background: "#fff",
      borderRadius: 8,
      minWidth: 80,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}
  >
    <div style={{ color, marginBottom: 4 }}>{icon}</div>
    <Text style={{ fontSize: 20, fontWeight: 700, color: "#232A36" }}>
      {count}
    </Text>
    <Text style={{ fontSize: 12, color: "#718096", fontWeight: 500 }}>
      {label}
    </Text>
  </div>
);

const TagList = ({ items, getLabel }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
    }}
  >
    {items.map((item, idx) => {
      const label = getLabel ? getLabel(item) : item;
      return (
        <Tag
          key={label + idx}
          style={{
            background: "#F5F5F5",
            color: "#232A36",
            fontWeight: 600,
            fontSize: 12,
            borderRadius: 4,
            margin: 0,
            padding: "2px 8px",
            border: "none",
          }}
        >
          {label}
        </Tag>
      );
    })}
  </div>
);

function AssignSampler({
  isAssignSamplerModalVisible,
  setIsAssignSamplerModalVisible,
  kitData,
  fetchKitLists,
  from = "",
}) {
  const dispatch = useDispatch();
  const states = useSelector(selectors.selectStates);
  const districts = useSelector(selectors.selectDistricts);
  const samplers = useSelector(selectors.selectSamplers);

  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedSampler, setSelectedSampler] = useState("");
  const [isAssignSamplerLoading, setIsAssignSamplerLoading] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);

  const kitNumber = useMemo(() => kitData?.samplerKit?.kitNumber, [kitData]);
  const status = useMemo(() => kitData?.samplerKit?.status?.label, [kitData]);

  // Group seals by type
  const sealsByType = useMemo(() => {
    const sampleSeals = kitData?.samplerKit?.sampleSeals;
    if (!Array.isArray(sampleSeals)) return [];

    const grouped = {};
    sampleSeals.forEach((seal) => {
      const typeLabel = seal.sealType?.label || "Unknown";
      const typeId = seal.sealType?._id || "unknown";
      if (!grouped[typeId]) {
        grouped[typeId] = { label: typeLabel, seals: [] };
      }
      grouped[typeId].seals.push(seal.sampleSealNumber);
    });

    return Object.values(grouped);
  }, [kitData]);

  // Total seal count from sealsByType
  const totalSeals = useMemo(() => {
    return sealsByType.reduce((acc, group) => acc + group.seals.length, 0);
  }, [sealsByType]);

  const failedSealsByType = useMemo(() => {
    const failedSealNumbers = kitData?.failedSealNumbers;
    if (!Array.isArray(failedSealNumbers)) return [];
    return failedSealNumbers.map((group) => ({
      label: group.sealTypeLabel,
      seals: group.failed?.map((s) => s.sealNumber) || [],
      count: group.count,
      reason: group.failed?.[0]?.reason || "",
    }));
  }, [kitData]);

  const totalFailedSeals = useMemo(() => {
    return failedSealsByType.reduce(
      (acc, group) => acc + (group.count || 0),
      0
    );
  }, [failedSealsByType]);

  const boxIds = useMemo(() => {
    const samplerBoxes = kitData?.samplerKit?.samplerBoxes;
    if (Array.isArray(samplerBoxes)) {
      return samplerBoxes.map((box) => box.boxId);
    }
    return [];
  }, [kitData]);

  const failedBoxes = useMemo(() => {
    const failedBoxNumbers = kitData?.failedBoxes;
    if (Array.isArray(failedBoxNumbers)) {
      return failedBoxNumbers.map((box) => box.boxNumber);
    }
    return [];
  }, [kitData]);

  // Sticker IDs
  const stickerIds = useMemo(() => {
    const sampleStickers = kitData?.samplerKit?.sampleStickers;
    if (Array.isArray(sampleStickers)) {
      return sampleStickers.map((sticker) => sticker.sampleStickerNumber);
    }
    return [];
  }, [kitData]);

  // Failed stickers
  const failedStickers = useMemo(() => {
    const failedStickerNumbers = kitData?.failedStickers;
    if (Array.isArray(failedStickerNumbers)) {
      return failedStickerNumbers.map((s) => s.stickerNumber);
    }
    return [];
  }, [kitData]);

  // Devices
  const devices = useMemo(() => {
    const deviceList = kitData?.samplerKit?.devices;
    if (Array.isArray(deviceList)) {
      return deviceList;
    }
    return [];
  }, [kitData]);

  // Failed devices
  const failedDevicesList = useMemo(() => {
    const failedDevices = kitData?.failedDevices;
    if (Array.isArray(failedDevices)) {
      return failedDevices;
    }
    return [];
  }, [kitData]);

  const handleKitAssignToSampler = () => {
    if (!selectedState) {
      message.error("Please select a state");
      return;
    }
    if (!selectedDistrict) {
      message.error("Please select a district");
      return;
    }
    if (!selectedSampler) {
      message.error("Please select a sampler");
      return;
    }

    setIsAssignSamplerLoading(true);
    const postData = [
      {
        stateId: selectedState,
        districtId: selectedDistrict,
        samplerId: selectedSampler,
        samplerKitId: kitData?.samplerKit?._id,
      },
    ];
    dispatch(
      actions.kitAssignToSampler({
        postData,
        onSuccess: () => {
          setIsSuccessModalVisible(true);
          resetState();
          message.success("Sampler assigned successfully");
        },
        onFailure: () => {
          setIsAssignSamplerLoading(false);
          message.error("Failed to assign sampler");
        },
      })
    );
  };
  // Reset local state
  const resetState = () => {
    setIsAssignSamplerLoading(false);
    setIsAssignSamplerModalVisible(false);
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedSampler("");
  };

  const agencyId = kitData?.samplerKit?.agency?._id;
  const stateId = kitData?.samplerKit?.state?._id;
  const districtId = kitData?.samplerKit?.district?._id;
  const samplerId = kitData?.samplerKit?.sampler?._id;
  const footer = (
    <Row justify="end" style={{ marginTop: 0 }}>
      <Space size={16}>
        <Button
          size="large"
          onClick={resetState}
          style={{
            borderRadius: 8,
            fontWeight: 700,
            border: "1px solid #D9D9D9",
            background: "#fff",
          }}
        >
          Close
        </Button>
        <Button
          type="primary"
          size="large"
          style={{
            borderRadius: 8,
            fontWeight: 700,
            background: "#23235F",
            color: "#fff",
          }}
          loading={isAssignSamplerLoading}
          // disabled={
          //   !selectedState || !selectedDistrict || !selectedSampler
          // }
          onClick={handleKitAssignToSampler}
        >
          {samplerId ? "Update Sampler" : "Assign Sampler"}
        </Button>
      </Space>
    </Row>
  );

  const stateOptions = useMemo(() => getStateFilters(states), [states]);
  const districtOptions = useMemo(
    () => getDistrictFilters(districts),
    [districts]
  );
  const samplerOptions = useMemo(() => getSamplerFilters(samplers), [samplers]);

  useEffect(() => {
    if (selectedState) {
      dispatch(actions.fetchDistricts({ stateId: selectedState }));
    }
  }, [selectedState, dispatch]);

  useEffect(() => {
    if (selectedState && selectedDistrict && agencyId) {
      dispatch(
        actions.fetchSamplers({
          stateId: selectedState,
          districtId: selectedDistrict,
          agencyId,
        })
      );
    }
  }, [selectedState, selectedDistrict, agencyId, dispatch]);

  useEffect(() => {
    if (isAssignSamplerModalVisible && samplerId) {
      setSelectedSampler(samplerId || "");
      setSelectedDistrict(districtId || "");
      setSelectedState(stateId || "");
    }
  }, [dispatch, isAssignSamplerModalVisible, stateId, districtId, samplerId]);

  return (
    <>
      <Modal
        visible={isAssignSamplerModalVisible}
        onCancel={resetState}
        title={
          <Title
            level={4}
            style={{ fontWeight: 700, margin: 0, color: "#232A36" }}
          >
            {samplerId ? "Update Sampler of Kit" : "Assign Sampler to Kit"}
          </Title>
        }
        footer={footer}
        width={850}
        bodyStyle={{
          borderRadius: 20,
          padding: 20,
          background: "#fff",
          maxHeight: "75vh",
          overflowY: "auto",
        }}
        style={{ borderRadius: 16 }}
        closeIcon={<IoCloseSharp size="24" />}
        centered
        className="modal-content"
      >
        {from === "action" ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Card
              style={{
                background: "#F8F9FA",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #E8E8E8",
              }}
              bodyStyle={{ padding: "20px" }}
            >
              {/* Kit Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <Title
                  level={4}
                  style={{ fontWeight: 800, margin: 0, color: "#232A36" }}
                >
                  {kitNumber}
                </Title>
                <Tag
                  style={{
                    background: "#FFF3E8",
                    color: "#FA8C16",
                    fontWeight: 600,
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 10px",
                    margin: 0,
                  }}
                >
                  {status}
                </Tag>
              </div>
              {(sealsByType?.length > 0 ||
                boxIds?.length > 0 ||
                stickerIds?.length > 0 ||
                devices?.length > 0) && (
                <Collapse
                  ghost
                  style={{
                    background: "rgba(255,255,255,0.8)",
                    borderRadius: 8,
                    marginTop: 16,
                    textAlign: "left",
                  }}
                >
                  {/* Seals Panel */}
                  {sealsByType?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#232A36" }}>
                          <FiTag style={{ marginRight: 8 }} />
                          Seal Details ({totalSeals})
                        </Text>
                      }
                      key="seals"
                    >
                      {sealsByType.map((group, groupIdx) => (
                        <div key={groupIdx} style={{ marginBottom: 12 }}>
                          <Text
                            style={{
                              fontWeight: 600,
                              color: "#718096",
                              fontSize: 13,
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            {group.label} ({group.seals.length})
                          </Text>
                          <TagList items={group.seals} />
                        </div>
                      ))}
                    </Panel>
                  )}

                  {/* Boxes Panel */}
                  {boxIds?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#232A36" }}>
                          <FiBox style={{ marginRight: 8 }} />
                          Box Details ({boxIds?.length})
                        </Text>
                      }
                      key="boxes"
                    >
                      <TagList items={boxIds} />
                    </Panel>
                  )}

                  {/* Stickers Panel */}
                  {stickerIds?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#232A36" }}>
                          <FiPackage style={{ marginRight: 8 }} />
                          Sticker Details ({stickerIds?.length})
                        </Text>
                      }
                      key="stickers"
                    >
                      <TagList items={stickerIds} />
                    </Panel>
                  )}

                  {/* Devices Panel */}
                  {devices?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#232A36" }}>
                          <FiCpu style={{ marginRight: 8 }} />
                          Device Details ({devices?.length})
                        </Text>
                      }
                      key="devices"
                    >
                      <TagList
                        items={devices}
                        getLabel={(d) => `${d.deviceName} (${d.uniqueId})`}
                      />
                    </Panel>
                  )}
                </Collapse>
              )}
            </Card>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Card
              style={{
                background: "#EAF7EA",
                borderRadius: "8px",
                textAlign: "center",
                border: "none",
              }}
              bodyStyle={{ padding: "20px" }}
            >
              {/* Success Header */}
              <MdDone size={36} color="#2E7D32" />
              <Title
                level={4}
                style={{ fontWeight: 800, marginBottom: 4, marginTop: 8 }}
              >
                Kit Successfully Created!
              </Title>
              <Text style={{ color: "#2E7D32", fontWeight: 600, fontSize: 16 }}>
                {kitNumber}
              </Text>
              <br />
              <Tag
                style={{
                  background: "#FFF3E8",
                  color: "#FA8C16",
                  fontWeight: 600,
                  border: "none",
                  marginTop: 8,
                }}
              >
                {status}
              </Tag>

              {/* Asset Summary Row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 12,
                  marginTop: 16,
                  flexWrap: "wrap",
                }}
              >
                {totalSeals > 0 && (
                  <AssetSummaryCard
                    icon={<FiTag size={20} />}
                    label="Seals"
                    count={totalSeals}
                    color="#1890ff"
                  />
                )}
                {boxIds?.length > 0 && (
                  <AssetSummaryCard
                    icon={<FiBox size={20} />}
                    label="Boxes"
                    count={boxIds.length}
                    color="#52c41a"
                  />
                )}
                {stickerIds?.length > 0 && (
                  <AssetSummaryCard
                    icon={<FiPackage size={20} />}
                    label="Stickers"
                    count={stickerIds.length}
                    color="#722ed1"
                  />
                )}
                {devices?.length > 0 && (
                  <AssetSummaryCard
                    icon={<FiCpu size={20} />}
                    label="Devices"
                    count={devices.length}
                    color="#13c2c2"
                  />
                )}
              </div>

              {(sealsByType?.length > 0 ||
                boxIds?.length > 0 ||
                stickerIds?.length > 0 ||
                devices?.length > 0) && (
                <Collapse
                  ghost
                  style={{
                    background: "rgba(255,255,255,0.6)",
                    borderRadius: 8,
                    marginTop: 16,
                    textAlign: "left",
                  }}
                >
                  {/* Seals Panel */}
                  {sealsByType?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#232A36" }}>
                          <FiTag style={{ marginRight: 8 }} />
                          Seal Details ({totalSeals})
                        </Text>
                      }
                      key="seals"
                    >
                      {sealsByType.map((group, groupIdx) => (
                        <div key={groupIdx} style={{ marginBottom: 12 }}>
                          <Text
                            style={{
                              fontWeight: 600,
                              color: "#718096",
                              fontSize: 13,
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            {group.label} ({group.seals.length})
                          </Text>
                          <TagList items={group.seals} />
                        </div>
                      ))}
                    </Panel>
                  )}

                  {/* Boxes Panel */}
                  {boxIds?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#232A36" }}>
                          <FiBox style={{ marginRight: 8 }} />
                          Box Details ({boxIds?.length})
                        </Text>
                      }
                      key="boxes"
                    >
                      <TagList items={boxIds} />
                    </Panel>
                  )}

                  {/* Stickers Panel */}
                  {stickerIds?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#232A36" }}>
                          <FiPackage style={{ marginRight: 8 }} />
                          Sticker Details ({stickerIds?.length})
                        </Text>
                      }
                      key="stickers"
                    >
                      <TagList items={stickerIds} />
                    </Panel>
                  )}

                  {/* Devices Panel */}
                  {devices?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#232A36" }}>
                          <FiCpu style={{ marginRight: 8 }} />
                          Device Details ({devices?.length})
                        </Text>
                      }
                      key="devices"
                    >
                      <TagList
                        items={devices}
                        getLabel={(d) => `${d.deviceName} (${d.uniqueId})`}
                      />
                    </Panel>
                  )}
                </Collapse>
              )}
            </Card>

            {/* Failed Items */}
            {(totalFailedSeals > 0 ||
              failedBoxes?.length > 0 ||
              failedStickers?.length > 0 ||
              failedDevicesList?.length > 0) && (
              <Card
                style={{
                  background: "#FFF2F0",
                  borderRadius: "8px",
                  border: "1px solid #FFCCC7",
                }}
                bodyStyle={{ padding: "16px" }}
              >
                <Text
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#CF1322",
                    display: "block",
                    marginBottom: 12,
                  }}
                >
                  <FiAlertTriangle
                    style={{ marginRight: 8, verticalAlign: "middle" }}
                  />
                  Some items failed to add
                </Text>

                <Collapse
                  ghost
                  style={{
                    background: "rgba(255,255,255,0.6)",
                    borderRadius: 8,
                    textAlign: "left",
                  }}
                >
                  {/* Failed Seals Panel */}
                  {totalFailedSeals > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#CF1322" }}>
                          <FiTag style={{ marginRight: 8 }} />
                          Failed Seals ({totalFailedSeals})
                          {failedSealsByType[0]?.reason
                            ? ` (${failedSealsByType[0].reason})`
                            : ""}
                        </Text>
                      }
                      key="failed-seals"
                    >
                      {failedSealsByType.map((group, groupIdx) =>
                        group.count > 0 ? (
                          <div key={groupIdx} style={{ marginBottom: 12 }}>
                            <Text
                              style={{
                                fontWeight: 600,
                                color: "#718096",
                                fontSize: 13,
                                display: "block",
                                marginBottom: 6,
                              }}
                            >
                              {group.label} ({group.count})
                            </Text>
                            <TagList items={group.seals} />
                          </div>
                        ) : null
                      )}
                    </Panel>
                  )}

                  {/* Failed Boxes Panel */}
                  {failedBoxes?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#CF1322" }}>
                          <FiBox style={{ marginRight: 8 }} />
                          Failed Boxes ({failedBoxes.length})
                          {kitData?.failedBoxNumbers?.[0]?.reason}
                        </Text>
                      }
                      key="failed-boxes"
                    >
                      <TagList items={failedBoxes} />
                    </Panel>
                  )}

                  {/* Failed Stickers Panel */}
                  {failedStickers?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#CF1322" }}>
                          <FiPackage style={{ marginRight: 8 }} />
                          Failed Stickers ({failedStickers.length})(
                          {kitData?.failedStickers?.[0]?.reason})
                        </Text>
                      }
                      key="failed-stickers"
                    >
                      <TagList items={failedStickers} />
                    </Panel>
                  )}

                  {/* Failed Devices Panel */}
                  {failedDevicesList?.length > 0 && (
                    <Panel
                      header={
                        <Text style={{ fontWeight: 600, color: "#CF1322" }}>
                          <FiCpu style={{ marginRight: 8 }} />
                          Failed Devices ({failedDevicesList.length})(
                          {kitData?.failedDevices?.[0]?.reason})
                        </Text>
                      }
                      key="failed-devices"
                    >
                      <TagList
                        items={failedDevicesList}
                        getLabel={(d) => `${d.deviceName} (${d.uniqueId})`}
                      />
                    </Panel>
                  )}
                </Collapse>
              </Card>
            )}
          </Space>
        )}

        <div style={{ padding: "20px 0px 0px 0px" }}>
          <Row gutter={[0, 12]}>
            <Col span={24}>
              <Text style={{ fontWeight: 600, color: "#232A36" }}>
                State <span style={{ color: "red" }}>*</span>
              </Text>
              <Select
                showSearch
                optionFilterProp="label"
                value={selectedState || "All States"}
                onChange={(value) => {
                  setSelectedState(value);
                  setSelectedDistrict("");
                  setSelectedSampler("");
                }}
                options={stateOptions}
                className="filter-select"
                style={{
                  width: "100%",
                  marginTop: 6,
                }}
                size="large"
              />
            </Col>
            <Col span={24}>
              <Text style={{ fontWeight: 600, color: "#232A36" }}>
                District <span style={{ color: "red" }}>*</span>
              </Text>
              <Tooltip
                title={!selectedState ? "Please select a state first" : ""}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  disabled={!selectedState}
                  value={selectedDistrict || "All Districts"}
                  onChange={(value) => {
                    setSelectedDistrict(value);
                    setSelectedSampler("");
                  }}
                  options={districtOptions}
                  className="filter-select"
                  style={{
                    width: "100%",
                    marginTop: 6,
                  }}
                  size="large"
                />
              </Tooltip>
            </Col>
            <Col span={24}>
              <Text style={{ fontWeight: 600, color: "#232A36" }}>
                Select Sampler <span style={{ color: "red" }}>*</span>
              </Text>
              <Tooltip
                title={
                  !selectedDistrict ? "Please select a district first" : ""
                }
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  disabled={!selectedDistrict}
                  value={selectedSampler || "All Samplers"}
                  onChange={(value) => {
                    setSelectedSampler(value);
                  }}
                  options={samplerOptions}
                  className="filter-select"
                  style={{
                    width: "100%",
                    marginTop: 6,
                  }}
                  size="large"
                />
              </Tooltip>
            </Col>
          </Row>
        </div>
      </Modal>
      <SuccessModal
        isSuccessModalVisible={isSuccessModalVisible}
        setIsSuccessModalVisible={setIsSuccessModalVisible}
        kitNumber={kitNumber}
        fetchKitLists={fetchKitLists}
        samplerId={samplerId}
      />
    </>
  );
}

export default AssignSampler;
